import React, { useState } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { supabase } from '@/services/supabase/supabaseClient';

interface MuteModalProps {
  visible: boolean;
  onClose: () => void;
  groupId: string;
  groupName: string;
  userId: string;
}

interface MuteOption {
  label: string;
  duration: number | null; // null = forever
  icon: 'time-outline' | 'moon-outline' | 'infinite-outline';
}

const MUTE_OPTIONS: MuteOption[] = [
  { label: '1 hour', duration: 1, icon: 'time-outline' },
  { label: '8 hours', duration: 8, icon: 'time-outline' },
  { label: '24 hours', duration: 24, icon: 'moon-outline' },
  { label: '1 week', duration: 168, icon: 'moon-outline' },
  { label: 'Forever', duration: null, icon: 'infinite-outline' },
];

export const MuteModal: React.FC<MuteModalProps> = ({
  visible,
  onClose,
  groupId,
  groupName,
  userId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMute = async (option: MuteOption) => {
    console.log(`🔕 [MuteModal] Muting group ${groupId} for ${option.label}`);
    setLoading(true);
    setError(null);

    try {
      // Calculate mute_until timestamp
      let muteUntil: string | null = null;
      if (option.duration !== null) {
        const now = new Date();
        now.setHours(now.getHours() + option.duration);
        muteUntil = now.toISOString();
      }

      // Update group_members notification preferences
      const { error: updateError } = await supabase
        .from('group_members')
        .update({
          notification_enabled: false,
          mute_until: muteUntil,
        })
        .eq('group_id', groupId)
        .eq('user_id', userId);

      if (updateError) {
        throw updateError;
      }

      console.log(`✅ [MuteModal] Successfully muted group until ${muteUntil || 'forever'}`);

      // Close modal
      onClose();
    } catch (err: any) {
      console.error(`❌ [MuteModal] Failed to mute group:`, err);
      setError('Failed to mute notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleUnmute = async () => {
    console.log(`🔔 [MuteModal] Unmuting group ${groupId}`);
    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('group_members')
        .update({
          notification_enabled: true,
          mute_until: null,
        })
        .eq('group_id', groupId)
        .eq('user_id', userId);

      if (updateError) {
        throw updateError;
      }

      console.log(`✅ [MuteModal] Successfully unmuted group`);

      // Close modal
      onClose();
    } catch (err: any) {
      console.error(`❌ [MuteModal] Failed to unmute group:`, err);
      setError('Failed to unmute notifications');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal visible={visible} animationType="slide" transparent={true} onRequestClose={onClose}>
      <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={onClose}>
        <TouchableOpacity
          style={styles.bottomSheet}
          activeOpacity={1}
          onPress={e => e.stopPropagation()}
        >
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.handleBar} />
            <Text style={styles.title}>Mute Notifications</Text>
            <Text style={styles.subtitle}>{groupName}</Text>
          </View>

          {/* Error Banner */}
          {error && (
            <View style={styles.errorBanner}>
              <Ionicons name="warning" size={16} color={Colors.status.error} />
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}

          {/* Mute Options */}
          <View style={styles.optionsContainer}>
            {MUTE_OPTIONS.map((option, index) => (
              <TouchableOpacity
                key={index}
                style={styles.option}
                onPress={() => handleMute(option)}
                disabled={loading}
                activeOpacity={0.7}
              >
                <View style={styles.optionIcon}>
                  <Ionicons name={option.icon} size={24} color={Colors.accent.primary} />
                </View>
                <Text style={styles.optionLabel}>{option.label}</Text>
                {loading ? (
                  <ActivityIndicator size="small" color={Colors.text.tertiary} />
                ) : (
                  <Ionicons name="chevron-forward" size={20} color={Colors.text.tertiary} />
                )}
              </TouchableOpacity>
            ))}

            {/* Divider */}
            <View style={styles.divider} />

            {/* Unmute Option */}
            <TouchableOpacity
              style={styles.option}
              onPress={handleUnmute}
              disabled={loading}
              activeOpacity={0.7}
            >
              <View style={styles.optionIcon}>
                <Ionicons name="notifications" size={24} color={Colors.status.success} />
              </View>
              <Text style={[styles.optionLabel, styles.unmuteLabe]}>Turn On Notifications</Text>
              {loading ? (
                <ActivityIndicator size="small" color={Colors.text.tertiary} />
              ) : (
                <Ionicons name="chevron-forward" size={20} color={Colors.text.tertiary} />
              )}
            </TouchableOpacity>
          </View>

          {/* Cancel Button */}
          <TouchableOpacity style={styles.cancelButton} onPress={onClose} disabled={loading}>
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  bottomSheet: {
    backgroundColor: Colors.background.primary,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: 40,
  },
  header: {
    paddingTop: 12,
    paddingHorizontal: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
    alignItems: 'center',
  },
  handleBar: {
    width: 40,
    height: 4,
    backgroundColor: Colors.text.tertiary,
    borderRadius: 2,
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: Colors.text.secondary,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: `${Colors.status.error}12`,
    borderLeftWidth: 3,
    borderLeftColor: Colors.status.error,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginHorizontal: 20,
    marginTop: 16,
    borderRadius: 8,
    gap: 8,
  },
  errorText: {
    flex: 1,
    fontSize: 13,
    color: Colors.status.error,
  },
  optionsContainer: {
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  optionIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Colors.background.tertiary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  optionLabel: {
    flex: 1,
    fontSize: 16,
    fontWeight: '500',
    color: Colors.text.primary,
  },
  unmuteLabe: {
    color: Colors.status.success,
  },
  divider: {
    height: 1,
    backgroundColor: Colors.border.subtle,
    marginVertical: 12,
  },
  cancelButton: {
    marginHorizontal: 20,
    marginTop: 12,
    paddingVertical: 14,
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text.primary,
  },
});
